import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';

const LIBRARIES: ('places')[] = ['places'];

const MAP_CONTAINER_STYLE: React.CSSProperties = {
  width: '100%',
  height: '260px',
  borderRadius: '12px',
};

const DEFAULT_CENTER = { lat: 25.7617, lng: -80.1918 }; // Miami

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: 'greedy',
  styles: [
    { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a3e' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

export interface PickupLocation {
  address: string;
  lat: number;
  lng: number;
}

interface PickupLocationPickerProps {
  value: PickupLocation | null;
  onChange: (location: PickupLocation | null) => void;
}

const PickupLocationPicker: React.FC<PickupLocationPickerProps> = ({ value, onChange }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: LIBRARIES,
  });

  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const autocompleteRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesRef = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    autocompleteRef.current = new google.maps.places.AutocompleteService();
    placesRef.current = new google.maps.places.PlacesService(map);
  }, []);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        onChange({ address: results[0].formatted_address, lat, lng });
      } else {
        onChange({ address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, lat, lng });
      }
    });
  }, [onChange]);

  const handleSearch = useCallback((input: string) => {
    setSearchInput(input);
    if (!input.trim() || !autocompleteRef.current) {
      setPredictions([]);
      return;
    }
    setSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input, types: ['address'] },
      (results, status) => {
        setSearching(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
        } else {
          setPredictions([]);
        }
      }
    );
  }, []);

  const selectPrediction = useCallback((prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesRef.current) return;
    placesRef.current.getDetails(
      { placeId: prediction.place_id, fields: ['geometry', 'formatted_address'] },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || prediction.description;
          onChange({ address, lat, lng });
          setSearchInput(address);
          setPredictions([]);
          mapRef.current?.panTo({ lat, lng });
          mapRef.current?.setZoom(16);
        }
      }
    );
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange(null);
    setSearchInput('');
    setPredictions([]);
  }, [onChange]);

  if (!isLoaded) {
    return (
      <div className="bg-BLACK/30 rounded-xl p-4 border border-WHITE/10">
        <div className="flex items-center gap-2 text-WHITE/50 text-sm">
          <div className="w-4 h-4 border-2 border-WHITE/30 border-t-transparent rounded-full animate-spin" />
          Loading map...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-BLACK/30 rounded-xl p-4 border border-WHITE/10 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs text-WHITE/50 uppercase tracking-wide">Your Pickup Location</h3>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-ORANGE hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Search bar */}
      <div className="relative">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search for an address..."
          className="w-full bg-BLACK/40 border border-WHITE/20 rounded-lg px-3 py-2.5 text-WHITE text-sm focus:border-ORANGE focus:outline-none placeholder-WHITE/30"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-WHITE/30 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {predictions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-BACKGROUND1 border border-WHITE/20 rounded-lg overflow-hidden shadow-lg max-h-48 overflow-y-auto">
            {predictions.map((p) => (
              <button
                key={p.place_id}
                type="button"
                onClick={() => selectPrediction(p)}
                className="w-full text-left px-3 py-2.5 text-sm text-WHITE hover:bg-WHITE/10 transition-colors border-b border-WHITE/5 last:border-0"
              >
                {p.description}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={value ? { lat: value.lat, lng: value.lng } : DEFAULT_CENTER}
        zoom={value ? 16 : 12}
        options={MAP_OPTIONS}
        onClick={handleMapClick}
        onLoad={onMapLoad}
      >
        {value && (
          <Marker
            position={{ lat: value.lat, lng: value.lng }}
          />
        )}
      </GoogleMap>

      <p className="text-WHITE/40 text-xs">
        Search for an address or tap the map to set your pickup location.
      </p>

      {value && (
        <div className="bg-ORANGE/10 border border-ORANGE/30 rounded-lg p-3">
          <p className="text-WHITE text-sm font-bold">{value.address}</p>
        </div>
      )}
    </div>
  );
};

export default PickupLocationPicker;
